import {
  AtlasContentEditorSerializedState,
  AtlasDialogRootProps,
  Button,
  Dialog,
  TextField,
  useContentEditor,
} from "@resource/atlas";
import { atlasArrowLeft } from "@resource/atlas/icons";
import { useLogEvent } from "analytics";
import { GuideUpdateTemplateEditor } from "client/components/guide-content/GuideUpdateEditor";
import { InsertInterviewsButton } from "client/guide-content/rich-blocks/interviews/__lib/InsertInterviewsButton";
import { RichBlockProviderOrganization } from "client/guide-content/rich-blocks/provider";
import { useInsertRequestAvailability } from "client/guide-content/rich-blocks/request-availability/__lib/use-insert-request-availability";
import { CollapsedSignature } from "components/CollapsedSignature";
import Line from "components/MessageComposer/Line";
import { RefObject, useRef } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { getTitlePlaceholder } from "shared/utils/placeholders";

export type PostTemplateContentDialogFormData = {
  data: AtlasContentEditorSerializedState;
  title: string;
};

export type PostTemplateContentDialogViewProps = {
  title: string;
  submitLoading?: boolean;
  submitButtonText: string;
  useFormReturn: UseFormReturn<PostTemplateContentDialogFormData>;
  onSubmit: (data: PostTemplateContentDialogFormData) => void;
  onBack?: () => void;
  dialogState?: NonNullable<AtlasDialogRootProps["state"]>;
  hideCancel?: boolean;
  allowNonDirtySubmission?: boolean;
  initialFocusRef: RefObject<HTMLInputElement>;
  organization?: RichBlockProviderOrganization | null;
  /** Type passed for logging events */
  analyticsType?: string;
};

export function PostTemplateContentDialogView(
  props: PostTemplateContentDialogViewProps
) {
  const {
    submitLoading,
    useFormReturn: {
      control,
      handleSubmit,
      formState: { errors, isDirty, isValid, isSubmitted },
    },
    onSubmit,
    onBack,
    title,
    submitButtonText,
    dialogState,
    allowNonDirtySubmission = false,
    initialFocusRef,
    organization,
    analyticsType,
    hideCancel,
  } = props;

  const editorInputRef = useRef<HTMLInputElement>(null);

  const logEvent = useLogEvent({
    component: "PostTemplateContentDialogView",
    project: "Post templates",
    ...(analyticsType && { type: analyticsType }),
  });
  const { editor, contentEditorProps } = useContentEditor();
  const { InsertButton: InsertRequestAvailabilityButton } =
    useInsertRequestAvailability({
      editor,
      logEvent,
    });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Dialog.View
        className="!min-h-[40rem]"
        header={{
          title,
          ...(onBack
            ? {
                backAction: (
                  <Button
                    isGhost
                    size="small"
                    icon={atlasArrowLeft}
                    aria-label="Close dialog"
                    onClick={onBack}
                  />
                ),
              }
            : {}),
        }}
        bodyProps={{
          className: "flex flex-col overflow-auto !px-0",
        }}
        footer={{
          ...(hideCancel
            ? {}
            : {
                leftActions: (
                  <Button
                    onClick={() => {
                      logEvent("Post Template Content Dialog Cancelled");
                      dialogState?.hide();
                    }}
                  >
                    Cancel
                  </Button>
                ),
              }),
          rightActions: (
            <Button
              onClick={() => {
                logEvent("Post Template Content Dialog Submitted");
              }}
              type="submit"
              variant="primary"
              disabled={(!allowNonDirtySubmission && !isDirty) || !isValid}
              isLoading={submitLoading}
            >
              {submitButtonText}
            </Button>
          ),
        }}
      >
        <div className="flex-shrink-0 px-6">
          <div className="flex gap-2 items-center mt-[2px]">
            <div className="text-body-sm-heavy w-12">Subject</div>
            <Controller
              render={({ field }) => (
                <TextField
                  {...field}
                  ref={initialFocusRef}
                  className="flex-1 ml-[0.625rem]"
                  isGhost
                  placeholder={getTitlePlaceholder()}
                />
              )}
              name="title"
              control={control}
            />
          </div>
          <Line />
          <div className="flex gap-1 mt-4">
            <InsertRequestAvailabilityButton className="!w-1/2" />
            <InsertInterviewsButton
              className="!w-1/2"
              editor={editor}
              logEvent={logEvent}
            />
          </div>
        </div>
        <Controller
          render={({ field }) => (
            <GuideUpdateTemplateEditor
              inputRef={editorInputRef}
              className="grow mt-4 flex flex-col overflow-auto px-6"
              placeholder="Write a message…"
              valueSet={{}}
              initialState={field.value}
              onChange={(editorState) => field.onChange(editorState.toJSON())}
              organization={organization}
              trailingContentSlot={
                <CollapsedSignature
                  className="mt-4"
                  companyLogoUrl={organization?.companyLogoUrl ?? ""}
                  companyName={organization?.name ?? "-"}
                />
              }
              {...contentEditorProps}
            />
          )}
          name="data"
          control={control}
          rules={{
            required: {
              value: true,
              message: "Content is required",
            },
            validate: (v) => {
              const nonEmptyNodeAtRoot = v?.root.children.find(
                // @ts-expect-error children should exist for this to be considered not empty.
                (node) => !!node.children?.length
              );
              return !!nonEmptyNodeAtRoot;
            },
          }}
        />
        {errors?.data && isSubmitted && (
          <p className="text-body-sm text-accent px-6 mt-2">
            Content is required
          </p>
        )}
      </Dialog.View>
    </form>
  );
}
